import { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import { PermissionModuleName } from "../constants/permissionModules";
import { getFromStorage, STORAGE_KEYS, Loader } from "../constants";

interface ProtectedRouteProps extends PropsWithChildren {
  module?: PermissionModuleName;
  alternateModules?: PermissionModuleName[];
  adminOnly?: boolean;
}

const ProtectedRoute = ({
  module,
  alternateModules = [],
  adminOnly,
  children,
}: ProtectedRouteProps) => {
  const userData = useAuth();
  const token = getFromStorage(STORAGE_KEYS.token);

  // If token is present but user profile is not yet fully loaded (missing role/permissions),
  // show a Loader instead of immediately redirecting to permission-denied.
  if (token && (!userData || !userData.role)) {
    return <Loader isLoad={true} />;
  }

  if (userData?.role === "ADMIN") {
    return <>{children}</>;
  }

  if (adminOnly) {
    return <Navigate to="/permission-denied" replace />;
  }

  if (!module) {
    return <>{children}</>;
  }

  const modulesToCheck = [module, ...alternateModules].filter(
    Boolean,
  ) as PermissionModuleName[];

  const permission = userData?.permission?.find(
    (ele: { name?: string; isView?: boolean }) =>
      modulesToCheck.includes(ele?.name as PermissionModuleName) && ele?.isView,
  );

  if (!permission?.isView) {
    return <Navigate to="/permission-denied" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
